import { ChangeDetectionStrategy, Component, inject, OnDestroy } from '@angular/core';
import { DialogService, DynamicDialogComponent, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import { SvgIconComponent } from 'angular-svg-icon';
import { DeviceService } from '@app/core/services/device/device.service';
import { TranslatePipe } from '@ngx-translate/core';
import { I18nTagFormatPipe } from '@app/core/i18n/pipes/i18n-tag-format.pipe';
import { ILocalizationTagKey } from '@app/core/interfaces/localization';

const DIALOG_AUTOCLOSE_TIMEOUT = 8000;
const DIALOG_ANIMATION_TIMEOUT = 1000;

@Component({
  selector: 'gc-dialog-zoom-help',
  templateUrl: './dialog-zoom-help.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [ButtonModule, SvgIconComponent, TranslatePipe, I18nTagFormatPipe],
})
export class DialogZoomHelpComponent implements OnDestroy {
  private ref = inject(DynamicDialogRef);
  private dialogService = inject(DialogService);
  private deviceService = inject(DeviceService);
  private instance: DynamicDialogComponent | undefined;
  private autoCloseTimeout?: ReturnType<typeof setTimeout>;
  private animationTimeout?: ReturnType<typeof setTimeout>;

  public scrollModifierKeyText = this.deviceService.isMacOS() ? '⌘ Cmd' : 'Ctrl';

  public dialogZoomHelpTagsConfig: Record<ILocalizationTagKey, string> = {
    I18N_TAG_STRONG: '<strong class="dialog-zoom-help-content">{{content}}</strong>',
  };

  constructor() {
    this.instance = this.dialogService.getInstance(this.ref);
    this.startAutocloseTimer();
  }

  public ngOnDestroy(): void {
    if (this.ref) {
      this.ref.close(false);
    }

    this.clearTimers();
  }

  public close(result: boolean): void {
    if (this.ref) {
      this.ref.close(result);
    }
  }

  public restartTimers() {
    this.removeFadeOut();
    this.clearTimers();
    this.startAutocloseTimer();
  }

  private clearTimers() {
    if (this.autoCloseTimeout) {
      clearTimeout(this.autoCloseTimeout);
    }

    if (this.animationTimeout) {
      clearTimeout(this.animationTimeout);
    }
  }

  private addFadeOut() {
    const element = this.instance?.el.nativeElement;
    element.classList.add('opacity-0', 'transition-opacity', `duration-[${DIALOG_ANIMATION_TIMEOUT}ms]`, 'ease-out');
  }

  private removeFadeOut() {
    const element = this.instance?.el.nativeElement;
    element.classList.remove('opacity-0', 'transition-opacity', `duration-[${DIALOG_ANIMATION_TIMEOUT}ms]`, 'ease-out');
  }

  private startAutocloseTimer() {
    this.autoCloseTimeout = setTimeout(() => {
      this.addFadeOut();
      this.animationTimeout = setTimeout(() => {
        this.close(false);
      }, DIALOG_ANIMATION_TIMEOUT);
    }, DIALOG_AUTOCLOSE_TIMEOUT - DIALOG_ANIMATION_TIMEOUT);
  }
}
